import { useId, useState, type ReactNode } from 'react';
import { cn } from '@core/utils/cn';

export interface FormFieldRenderProps {
  id: string;
  describedById: string;
  /** Apply to the control so the field can track focus for the floating label. */
  onFocus: () => void;
  onBlur: () => void;
  'aria-invalid': boolean | undefined;
  'aria-describedby': string | undefined;
  'aria-required': boolean | undefined;
  disabled: boolean | undefined;
  className: string;
}

export interface FormFieldProps {
  label: string;
  /** Render the control. Spread the supplied props onto it. */
  children: (props: FormFieldRenderProps) => ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** mat-hint. Hidden while an error is showing, as Material does. */
  hint?: string;
  /** mat-error message. Presence switches the field to its invalid style. */
  error?: string;
  /** matPrefix content. */
  prefix?: ReactNode;
  /** matSuffix content — icon buttons, inline spinners (`diameter="18"`). */
  suffix?: ReactNode;
  /**
   * Set true when the control can never show a placeholder in the notch position —
   * selects, comboboxes, native date inputs. Keeps the label permanently raised, which
   * is what Material does for those controls.
   */
  alwaysRaised?: boolean;
  /** True when the control holds a value. Drives the floating-label position. */
  hasValue?: boolean;
  className?: string;
  /** Reserve no space for hint/error text (dense table filters). */
  dense?: boolean;
}

/**
 * The outlined form field. See `styles/field.css` for the notched-outline technique
 * and the effective-metrics rationale.
 *
 * Uses a render prop rather than cloning children because the control needs the
 * generated id, the aria wiring, and the focus callbacks — and because the control can
 * be an `<input>`, a `<textarea>`, or a combobox trigger button.
 *
 * ```tsx
 * <FormField label="Email" required error={errors.email?.message} hasValue={!!value}>
 *   {(field) => <input {...field} {...register('email')} type="email" />}
 * </FormField>
 * ```
 */
export function FormField({
  label,
  children,
  required,
  disabled,
  hint,
  error,
  prefix,
  suffix,
  alwaysRaised,
  hasValue,
  className,
  dense,
}: FormFieldProps) {
  const [focused, setFocused] = useState(false);
  const id = useId();
  const assistId = `${id}-assist`;

  const raised = focused || !!hasValue || !!alwaysRaised;
  const invalid = !!error;

  return (
    <div
      className={cn('vm-field', className)}
      data-focused={focused || undefined}
      data-raised={raised || undefined}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      /*
       * ⚠️ NEEDED BY CSS, not by JS. The floating label is absolutely positioned at a fixed `left`, so
       * with a prefix it rendered ON TOP of the affix icon — measured at label 12→86px over an icon at
       * 10→28px. CSS cannot tell a prefix affix from a suffix affix (both carry `.vm-field-affix`), so
       * the fact is published here instead of inferred with a fragile `:has()` selector.
       */
      data-prefixed={prefix ? 'true' : undefined}
    >
      <div className="vm-field-outline">
        <fieldset aria-hidden="true">
          <legend>{label}</legend>
        </fieldset>

        {prefix && <span className="vm-field-affix">{prefix}</span>}

        {children({
          id,
          describedById: assistId,
          onFocus: () => setFocused(true),
          onBlur: () => setFocused(false),
          'aria-invalid': invalid || undefined,
          'aria-describedby': hint || error ? assistId : undefined,
          'aria-required': required || undefined,
          disabled,
          className: 'vm-field-control',
        })}

        {suffix && <span className="vm-field-affix">{suffix}</span>}
      </div>

      {/*
        The text is its own span so only IT is cut off with "…" — the required asterisk (`::after` on
        the label) sits beside it and always shows. It used to be truncated with the text, so a long
        label on an empty field lost its "*" and the field looked optional until something was typed.
      */}
      <label htmlFor={id} className={cn('vm-field-label', required && 'vm-field-required')}>
        <span className="vm-field-label-text">{label}</span>
      </label>

      {!dense && (
        <span
          id={assistId}
          className={cn('vm-field-assist', invalid ? 'vm-field-error' : 'vm-field-hint')}
          // Errors must be announced; hints are read via aria-describedby only.
          role={invalid ? 'alert' : undefined}
        >
          {error ?? hint ?? ''}
        </span>
      )}
    </div>
  );
}
